import * as React from 'react';
import Popover from 'react-tiny-popover';
import MediaUploader from './MediaUploader';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import MediaItem from './MediaItem';
import MediaGallery from './MediaGallery';
import Loader from '../../../common/Loader/Loader';

interface MediaManagerProps {
    children: JSX.Element;
    showMediaManager: boolean;
    isMultiple?: boolean;
    type?: string;
    showMediaLibrary?: boolean;
    disableOutsideClick?: boolean;
    toggleMediaManager: (toggle: boolean) => void;
    applyCurrentMedia?: (data: {
        mediaURL: string;
        label?: string;
        type?: string;
    }) => void;

    onConfirm?: (media: any, hasMultipleMedia: boolean) => void;
}

const MediaManager: React.FC<MediaManagerProps> = ({
    children,
    showMediaManager,
    isMultiple,
    type = 'default',
    showMediaLibrary,
    disableOutsideClick,
    toggleMediaManager,
    applyCurrentMedia,
    onConfirm
}: MediaManagerProps) => {
    const defaultUploadMedia = {
        uploadedFileIds: [],
        uploadedFiles: {}
    };
    const [uploadedMedia, setUploadedMedia] =
        React.useState(defaultUploadMedia);
    const [isLoading, setIsLoading] = React.useState(false);
    const handleUploadMedia = (
        id: string,
        status: 'uploading' | 'complete',
        url?: string,
        filename?: string,
        type?: string
    ) => {
        if (status === 'uploading') {
            const imageObject = {
                id,
                label: '',
                selectedTags: [],
                isUploading: true,
                mediaURL: '',
                value: '',
                type: '',
                error: {
                    title: '',
                    selectedTags: '',
                    isError: false
                }
            };
            setUploadedMedia((media) => ({
                uploadedFileIds: [...media.uploadedFileIds, id],
                uploadedFiles: { ...media.uploadedFiles, [id]: imageObject }
            }));
        } else {
            setUploadedMedia((media) => ({
                ...media,
                uploadedFiles: {
                    ...media.uploadedFiles,
                    [id]: {
                        ...media.uploadedFiles[id],
                        label: filename,
                        isUploading: false,
                        mediaURL: url,
                        value: url,
                        type
                    }
                }
            }));

            if (!isMultiple) {
                applyCurrentMedia({ mediaURL: url, label: filename, type });
            }
        }
    };
    return (
        <Popover
            isOpen={showMediaManager}
            position={['right', 'left', 'bottom', 'top']}
            onClickOutside={() =>
                !disableOutsideClick && toggleMediaManager(false)
            }
            content={
                <div
                    className="popover--container"
                    onContextMenu={(e) => e.stopPropagation()}
                >
                    {isLoading && (
                        <>
                            <div className="popover--background-blur">
                                <Loader />
                            </div>
                        </>
                    )}
                    <div className="popover--container--header">
                        <div className="popover--container--header--title">
                            Media Gallery
                        </div>
                        <button
                            type="button"
                            className="popover--container--header--close"
                            data-dismiss="modal"
                            aria-label="Close"
                            onClick={(e) => {
                                e.stopPropagation();
                                !disableOutsideClick &&
                                    toggleMediaManager(false);
                            }}
                        >
                            <span aria-hidden="true">
                                <i className="nx nx-close"></i>
                            </span>
                        </button>
                    </div>
                    <div className="popover--container--body">
                        <Tabs defaultIndex={0}>
                            <TabList>
                                <Tab>Upload</Tab>
                                {showMediaLibrary && <Tab>My Files</Tab>}
                            </TabList>
                            <TabPanel>
                                <div
                                    className="media-item--container"
                                    style={{
                                        display: `${
                                            !uploadedMedia.uploadedFileIds
                                                .length
                                                ? 'block'
                                                : 'flex'
                                        }`
                                    }}
                                >
                                    {uploadedMedia.uploadedFileIds.length
                                        ? uploadedMedia.uploadedFileIds.map(
                                              (fileId) => (
                                                  <MediaItem
                                                      media={
                                                          uploadedMedia
                                                              .uploadedFiles[
                                                              fileId
                                                          ]
                                                      }
                                                      key={fileId}
                                                      applyCurrentMedia={() =>
                                                          applyCurrentMedia({
                                                              mediaURL:
                                                                  uploadedMedia
                                                                      .uploadedFiles[
                                                                      fileId
                                                                  ].mediaURL,
                                                              label: uploadedMedia
                                                                  .uploadedFiles[
                                                                  fileId
                                                              ].label,
                                                              type: uploadedMedia
                                                                  .uploadedFiles[
                                                                  fileId
                                                              ].type
                                                          })
                                                      }
                                                  />
                                              )
                                          )
                                        : null}
                                    {!uploadedMedia.uploadedFileIds.length ||
                                    isMultiple ? (
                                        <MediaUploader
                                            type={type}
                                            isMultiple={isMultiple}
                                            handleUploadMedia={
                                                handleUploadMedia
                                            }
                                            size={
                                                !uploadedMedia.uploadedFileIds
                                                    .length
                                                    ? 'full'
                                                    : 'single'
                                            }
                                        />
                                    ) : null}
                                </div>
                            </TabPanel>
                            {showMediaLibrary && (
                                <TabPanel>
                                    <MediaGallery
                                        type={type}
                                        setLoading={setIsLoading}
                                        applyCurrentMedia={applyCurrentMedia}
                                    />
                                </TabPanel>
                            )}
                        </Tabs>
                    </div>
                    {onConfirm && (
                        <div className="popover--container--footer">
                            <button
                                className="btn btn--sm btn--blue"
                                onClick={() => {
                                    onConfirm(
                                        Object.values(
                                            uploadedMedia.uploadedFiles
                                        ),
                                        true
                                    );
                                    toggleMediaManager(false);
                                    setUploadedMedia(defaultUploadMedia);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    )}
                </div>
            }
        >
            {children}
        </Popover>
    );
};

export default MediaManager;
